<template>
  <div class="login-container">
    <div v-if="isLoggedIn" class="user-info">
      <span class="user-name">{{ user.displayName || user.email }}</span>
      <button class="black-button logout-button" @click="logout">Sign out</button>
    </div>

    <div v-else class="login-options">
      <div class="provider-buttons">
        <button class="black-button provider-button" @click="onProviderLogin(loginWithGoogle, 'Google')">
          Sign in with Google
        </button>
        <button class="black-button provider-button" @click="onProviderLogin(loginWithMicrosoft, 'Microsoft')">
          Sign in with Microsoft
        </button>
      </div>

      <div class="magic-link-section">
        <form v-if="!magicLinkSent" @submit.prevent="onSendMagicLink" class="magic-link-form">
          <input
              v-model="email"
              type="email"
              placeholder="Or sign in with email"
              class="magic-link-input"
              required
          />
          <button type="submit" class="black-button magic-link-submit" :disabled="sending || !isValidEmail">
            {{ sending ? 'Sending…' : 'Send link' }}
          </button>
        </form>
        <p v-else class="magic-link-sent">
          Check your inbox — we sent a sign-in link to {{ email }}.
        </p>
      </div>

      <p v-if="loginError" class="login-error">{{ loginError }}</p>
    </div>
  </div>
</template>

<script setup>
import {computed, ref} from 'vue';
import {useAuth} from '@/composables/useAuth';

const {user, isLoggedIn, loginWithGoogle, loginWithMicrosoft, sendMagicLink, logout} = useAuth();

const email = ref('');
const isValidEmail = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value));
const sending = ref(false);
const magicLinkSent = ref(false);
const loginError = ref(null);

async function onProviderLogin(loginFn, name) {
  loginError.value = null;
  try {
    await loginFn();
  } catch (err) {
    console.error(`${name} login failed`, err);
    loginError.value = 'Sign in failed. Please try again.';
  }
}

async function onSendMagicLink() {
  loginError.value = null;
  sending.value = true;
  try {
    await sendMagicLink(email.value);
    magicLinkSent.value = true;
  } catch (err) {
    console.error('Magic link failed', err);
    loginError.value = 'Could not send sign-in link. Please try again.';
  } finally {
    sending.value = false;
  }
}
</script>

<style scoped>
.login-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 16px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-name {
  font-size: 14px;
  color: var(--text-color-bright);
}

.logout-button {
  min-width: unset;
}

.login-options {
  display: inline-flex;
  flex-direction: column;
  gap: 16px;
}

.provider-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.provider-button {
  white-space: nowrap;
  width: 100%;
}

.magic-link-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.magic-link-form {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.magic-link-input {
  background: transparent;
  border: 1px solid var(--highlighted);
  color: var(--text-color-bright);
  padding: 0 10px;
  height: 40px;
  font-size: 15px;
  outline: none;
  min-width: 220px;
}

.magic-link-input::placeholder {
  color: var(--text-color);
}

.magic-link-submit {
  min-width: unset;
}

@media screen and (max-width: 639px) {
  .login-options {
    width: 100%;
  }

  .magic-link-form {
    flex-direction: column;
    gap: 16px;
  }

  .magic-link-input {
    width: 100%;
    min-width: unset;
    box-sizing: border-box;
  }

  .magic-link-submit {
    width: 100%;
  }
}

.magic-link-sent {
  font-size: 13px;
  color: var(--text-color);
}

.login-error {
  margin-top: 4px;
  font-size: 13px;
  color: var(--geogaddi-r);
}
</style>